import { useState, useEffect, useRef } from "react";
import { X, ChevronLeft, Play, AlertCircle } from "lucide-react";
import axios from "axios";
import "react-toastify/dist/ReactToastify.css";
import { ToastContainer, toast } from "react-toastify";

export default function Photo() {
  const [media, setMedia] = useState({ images: [], videos: [] });
  const [loading, setLoading] = useState(true);
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [debugMode, setDebugMode] = useState(false);
  const [imageErrors, setImageErrors] = useState({});
  const videoRef = useRef(null);

  useEffect(() => {
    const fetchMedia = async () => {
      try {
        // Add a timestamp to avoid caching issues
        const response = await axios.get(
          `${process.env.REACT_APP_BASE_URL}/api/v1/gallery/media?t=${Date.now()}`
        );
        console.log("Media Data:", response.data);

        // Process the media data from the API response
        const mediaData = response.data;

        // Extract images
        const images = mediaData.flatMap(
          (item) =>
            item?.images?.map((image) => ({
              id: image,
              src: `${process.env.REACT_APP_BASE_URL}/api/uploads/images/${image}`,
              alt: `Image: ${image}`,
              title: `Image ${image.split("-")[1] || ""}`,
              type: "image",
            })) || []
        );

        // Extract videos
        const videos = mediaData.flatMap(
          (item) =>
            item?.videos?.map((video) => ({
              id: video,
              src: `${process.env.REACT_APP_BASE_URL}/api/uploads/videos/${video}`,
              alt: `Video: ${video}`,
              title: `Video ${video.split("-")[1] || ""}`,
              type: "video",
            })) || []
        );

        console.log("Processed Images:", images);
        console.log("Processed Videos:", videos);

        setMedia({ images, videos });
      } catch (error) {
        console.error("Error fetching media:", error);
        toast.error("Error fetching media. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchMedia();
  }, [process.env.REACT_APP_BASE_URL]);

  // Function to handle image errors with more detailed logging
  const handleImageError = (item) => (e) => {
    console.error(`Failed to load image: ${item.src}`);

    // Add to error tracking
    setImageErrors((prev) => ({
      ...prev,
      [item.id]: true,
    }));

    // Let's manually check the URL
    fetch(item.src, { method: "HEAD" })
      .then((response) => {
        console.log(`Image ${item.id} fetch status:`, response.status);
      })
      .catch((err) => {
        console.error(`Image ${item.id} fetch error:`, err);
      });
  };

  const handleMediaClick = (media) => {
    setSelectedMedia(media);
  };

  const handleCloseModal = () => {
    setSelectedMedia(null);
    if (videoRef.current) {
      videoRef.current.pause();
    }
  };

  const handleCancelVideo = () => {
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
    setSelectedMedia(null);
  };

  const toggleDebugMode = () => {
    setDebugMode(!debugMode);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        Loading...
      </div>
    );
  }

  const allMedia = [...media.images, ...media.videos];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <header className="text-white bg-blue-900">
        <div className="container px-4 mx-auto">
          <div className="flex items-center justify-between h-12">
            <h1 className="text-2xl font-bold">Our Latest Gallery</h1>
            {/* <button
              onClick={toggleDebugMode}
              className="px-3 py-1 text-xs rounded bg-blue-700 hover:bg-blue-600"
            >
              {debugMode ? "Hide Debug" : "Show Debug"}
            </button> */}
          </div>
        </div>
      </header>

      <div className="container px-4 py-12 mx-auto md:px-24">
        {allMedia.length === 0 ? (
          <div className="flex flex-col items-center justify-center p-8 bg-white rounded-lg shadow-md">
            <p className="text-xl text-gray-600">
              No media found in the gallery.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
            {allMedia.map((item) => (
              <div
                key={item.id}
                className="relative overflow-hidden rounded-lg shadow-lg cursor-pointer group"
                onClick={() => handleMediaClick(item)}
              >
                <div className="w-full h-0 pb-[100%] relative">
                  {item.type === "image" ? (
                    <>
                      <img
                        src={item.src}
                        alt={item.alt}
                        className="absolute inset-0 object-cover w-full h-full transition-all duration-300 group-hover:scale-110 group-hover:rotate-3"
                        onError={handleImageError(item)}
                      />
                      {imageErrors[item.id] && (
                        <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-200">
                          <AlertCircle className="w-8 h-8 mb-2 text-red-500" />
                          <p className="text-sm text-gray-700">
                            Image not found
                          </p>
                          {/* {debugMode && (
                            <div className="p-2 mt-2 text-xs text-left bg-gray-100 rounded">
                              <p className="text-gray-600 break-all">
                                {item.src}
                              </p>
                            </div>
                          )} */}
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      <video
                        src={item.src}
                        className="absolute inset-0 object-cover w-full h-full transition-all duration-300 group-hover:scale-110 group-hover:rotate-3"
                        muted
                        playsInline
                        onError={(e) => {
                          console.error(`Failed to load video: ${item.src}`);
                          setImageErrors((prev) => ({
                            ...prev,
                            [item.id]: true,
                          }));
                        }}
                      />
                      {imageErrors[item.id] ? (
                        <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-200">
                          <AlertCircle className="w-8 h-8 mb-2 text-red-500" />
                          <p className="text-sm text-gray-700">
                            Video not found
                          </p>
                          {debugMode && (
                            <div className="p-2 mt-2 text-xs text-left bg-gray-100 rounded">
                              <p className="text-gray-600 break-all">
                                {item.src}
                              </p>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="absolute inset-0 flex items-center justify-center transition-opacity duration-300 bg-black opacity-0 bg-opacity-40 group-hover:opacity-100">
                          <Play size={48} className="text-white" />
                        </div>
                      )}
                    </>
                  )}
                </div>
                <div className="absolute inset-0 flex items-center justify-center transition-opacity duration-300 bg-black bg-opacity-0 group-hover:bg-opacity-50">
                  <h3 className="text-lg font-semibold text-white transition-opacity duration-300 opacity-0 group-hover:opacity-100">
                    {item.title}
                  </h3>
                </div>
                {debugMode && (
                  <div className="absolute bottom-0 left-0 right-0 p-1 text-xs text-white bg-black bg-opacity-75">
                    ID: {item.id}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {debugMode && allMedia.length > 0 && (
          <div className="p-4 mt-8 bg-white rounded-lg shadow-md">
            <h2 className="mb-2 text-lg font-bold">Debug Information</h2>
            <div className="p-2 overflow-auto bg-gray-100 rounded-md max-h-64">
              <p className="mb-2 font-semibold">Base URL: {process.env.REACT_APP_BASE_URL}</p>
              <p className="mb-2 font-semibold">Image URLs:</p>
              <ul className="pl-4 list-disc">
                {media.images.map((img) => (
                  <li key={img.id} className="mb-1 break-all">
                    <span
                      className={
                        imageErrors[img.id] ? "text-red-500" : "text-green-500"
                      }
                    >
                      {img.src}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </div>

      {selectedMedia && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75">
          <div className="relative w-full max-w-2xl mx-4">
            <button
              onClick={handleCloseModal}
              className="absolute p-2 text-white bg-gray-800 rounded-full top-4 right-4 hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
              aria-label="Close modal"
            >
              <X size={24} />
            </button>
            <button
              onClick={handleCloseModal}
              className="absolute p-2 text-white bg-gray-800 rounded-full top-4 left-4 hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
              aria-label="Back to gallery"
            >
              <ChevronLeft size={24} />
            </button>
            {selectedMedia.type === "image" ? (
              <>
                <img
                  src={selectedMedia.src}
                  alt={selectedMedia.alt}
                  className="w-full h-auto rounded-lg"
                  onError={handleImageError(selectedMedia)}
                />
                {imageErrors[selectedMedia.id] && (
                  <div className="flex flex-col items-center justify-center p-8 bg-gray-200 rounded-lg">
                    <AlertCircle className="w-12 h-12 mb-2 text-red-500" />
                    <p className="text-gray-700">Image not found</p>
                    {debugMode && (
                      <div className="p-2 mt-4 text-sm text-left bg-gray-100 rounded">
                        <p className="text-gray-600 break-all">
                          {selectedMedia.src}
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </>
            ) : (
              <div className="relative w-full max-w-2xl mx-auto">
                <video
                  ref={videoRef}
                  src={selectedMedia.src}
                  controls
                  className="w-full h-auto rounded-lg"
                  autoPlay
                  onError={(e) => {
                    toast.error("Failed to load video. Please try again.");
                    setImageErrors((prev) => ({
                      ...prev,
                      [selectedMedia.id]: true,
                    }));
                  }}
                />
                {imageErrors[selectedMedia.id] && (
                  <div className="flex flex-col items-center justify-center p-8 bg-gray-200 rounded-lg">
                    <AlertCircle className="w-12 h-12 mb-2 text-red-500" />
                    <p className="text-gray-700">Video not found</p>
                    {debugMode && (
                      <div className="p-2 mt-4 text-sm text-left bg-gray-100 rounded">
                        <p className="text-gray-600 break-all">
                          {selectedMedia.src}
                        </p>
                      </div>
                    )}
                  </div>
                )}
                <button
                  onClick={handleCloseModal}
                  className="absolute p-2 text-white bg-gray-800 rounded-full top-4 right-4 hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                  aria-label="Close video"
                >
                  <X size={24} />
                </button>
                <button
                  onClick={handleCancelVideo}
                  className="absolute px-4 py-2 text-white bg-red-600 rounded-md bottom-4 left-4 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      <ToastContainer />
    </div>
  );
}
